export function readConditions(conditions) {
  if (!conditions.trim()) return {};

  try {
    const parsed = JSON.parse(conditions);

    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

const OPERATOR_WORDS = {
  $eq: '=',
  $neq: '≠',
  $gt: '>',
  $gte: '≥',
  $lt: '<',
  $lte: '≤',
  $in: '∈',
  $nin: '∉',
  $contains: '⊃',
  $containsi: '⊃',
  $startsWith: '^',
  $endsWith: '…',
};

export function summarizeConditions(conditions, joiners) {
  const branches = combinedBranches(conditions);

  if (branches) {
    if (branches.list.length === 0) return null;

    const clauses = branches.list.map((branch) => plainClauses(branch, joiners.and));

    if (clauses.some((clause) => clause === null)) return null;

    return clauses.join(` ${joiners[branches.joiner]} `);
  }

  return plainClauses(conditions, joiners.and);
}

function combinedBranches(conditions) {
  const keys = Object.keys(conditions);

  if (keys.length !== 1 || !['$or', '$and'].includes(keys[0])) return null;

  const list = conditions[keys[0]];

  if (!Array.isArray(list)) return null;

  return { joiner: keys[0] === '$or' ? 'or' : 'and', list };
}

function plainClauses(conditions, joiner) {
  if (!conditions || typeof conditions !== 'object' || Array.isArray(conditions)) return null;

  const lists = Object.entries(conditions).map(([key, value]) => clausesOf(key, value));

  if (lists.length === 0 || lists.some((clauses) => clauses === null)) return null;

  return lists.flat().join(` ${joiner} `);
}

function clausesOf(path, value) {
  if (path.startsWith('$')) return null;
  if (value === null || typeof value !== 'object') return [`${path} = ${JSON.stringify(value)}`];
  if (Array.isArray(value)) return null;

  const operator = singleOperator(value);

  if (operator) return operatorClause(path, operator, value[operator]);

  return nestedClauses(path, value);
}

function singleOperator(value) {
  const keys = Object.keys(value);

  if (keys.length !== 1 || !keys[0].startsWith('$')) return null;

  return keys[0];
}

function nestedClauses(path, value) {
  const entries = Object.entries(value);

  if (entries.length === 0) return null;

  const lists = entries.map(([key, child]) => (key.startsWith('$') ? null : clausesOf(`${path}.${key}`, child)));

  if (lists.some((clauses) => clauses === null)) return null;

  return lists.flat();
}

function operatorClause(path, operator, argument) {
  if (operator === '$exist') return [argument ? `${path} ∃` : `${path} ∄`];
  if (!OPERATOR_WORDS[operator]) return null;

  const listed = Array.isArray(argument) && ['$in', '$nin'].includes(operator);

  if (argument !== null && typeof argument === 'object' && !listed) return null;

  return [`${path} ${OPERATOR_WORDS[operator]} ${JSON.stringify(argument)}`];
}
